// Case conversion helpers

export const camelToSnake = (str: string): string =>
    str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)

export const camelToKebab = (str: string): string =>
    str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)

export const camelToConstant = (str: string): string =>
    str.replace(/[A-Z]/g, letter => `_${letter}`).toUpperCase()

export const snakeToCamel = (str: string): string =>
    str.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())

/**
 * Get an attribute from an element, trying multiple case formats.
 * Tries: camelCase, snake_case, kebab-case, CONSTANT_CASE
 *
 * @param element - The HTML element to get the attribute from
 * @param camelCaseName - The attribute name in camelCase format
 * @returns The attribute value or null if not found
 */
export const getAttributeAnyCasing = (element: Element, camelCaseName: string): string | null => {
    // Try camelCase first (original)
    let value = element.getAttribute(camelCaseName)
    if (value !== null) return value

    // Try snake_case
    value = element.getAttribute(camelToSnake(camelCaseName))
    if (value !== null) return value

    // Try kebab-case
    value = element.getAttribute(camelToKebab(camelCaseName))
    if (value !== null) return value

    // Try CONSTANT_CASE
    value = element.getAttribute(camelToConstant(camelCaseName))
    if (value !== null) return value

    return null
}

type CaseMap<T extends string> = { [key: string]: T }

/**
 * Generate a map that maps all case variations to the original camelCase key.
 * Useful for URL parameter parsing or config object normalization.
 *
 * @param keys - Array of camelCase keys
 * @returns A map where all case variations point to the original key
 */
export const generateCaseMap = <T extends string>(keys: T[]): CaseMap<T> => {
    const map: CaseMap<T> = {}

    for (const key of keys) {
        // camelCase (original)
        map[key] = key
        // snake_case
        map[camelToSnake(key)] = key
        // kebab-case
        map[camelToKebab(key)] = key
        // CONSTANT_CASE
        map[camelToConstant(key)] = key
    }

    return map
}
